import { readSupabaseInvokeError } from "@/lib/readSupabaseInvokeError";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import type { Resource } from "@/types/resource";
import intertwinedTreesPhoto from "@/assets/resources/intertwined-trees.jpg";
import lighthouseAnchorPhoto from "@/assets/resources/lighthouse-anchor.jpg";

export type ConversationResourceInvokeType = "pdf" | "voice_note";

export const conversationResourcesChangedEvent = "heybodhi:conversation-resources-changed";

export function notifyConversationResourcesChanged(): void {
  conversationResourcesUnavailable = false;
  window.dispatchEvent(new CustomEvent(conversationResourcesChangedEvent));
}

type ConvRow = Database["public"]["Tables"]["conversation_resources"]["Row"];

const BUCKET = "conversation-resources";

/** After one confirmed “table missing” response, skip further REST calls this tab session (avoids 404 spam). */
let conversationResourcesUnavailable = false;

function isMissingConversationResourcesRelation(error: { message?: string } | null): boolean {
  const msg = error?.message ?? "";
  if (!/conversation_resources/i.test(msg)) return false;
  return (
    /does not exist/i.test(msg) ||
    /could not find/i.test(msg) ||
    /schema cache/i.test(msg)
  );
}

/** Static images for striped cards (parity with Generated-for-you placeholders). */
function pickPhoto(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(31, h) + seed.charCodeAt(i)!;
    h |= 0;
  }
  return Math.abs(h) % 2 === 0 ? intertwinedTreesPhoto : lighthouseAnchorPhoto;
}

function resourceTypeLabel(t: string): string {
  if (t === "voice_note") return "Voice note";
  if (t === "docx") return "Word handout";
  return "Handout";
}

function resourceCardDescription(t: string): string {
  if (t === "voice_note") return "Voice note · from your mentoring chat";
  if (t === "docx") return "Editable Word handout · from your mentoring chat";
  return "One-page handout · from your mentoring chat";
}

function storagePathForRow(row: ConvRow): string | null {
  return row.pdf_storage_path ?? row.docx_storage_path ?? row.audio_storage_path;
}

/** Fresh signed URL for a private conversation-resources object. */
export async function refreshConversationResourceSignedUrl(
  storagePath: string,
  expiresIn = 86_400
): Promise<string | null> {
  const { data: signed, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, expiresIn);
  if (error || !signed?.signedUrl) {
    console.warn("[conversationResourceService] signed URL:", error?.message);
    return null;
  }
  return signed.signedUrl;
}

/** Dedupe concurrent stripe loads (e.g. React Strict Mode double-mount). */
let conversationStripeInflight: Promise<Resource[]> | null = null;

/** Signed URLs for the Resources stripe (private bucket). */
export async function fetchConversationStripeResources(): Promise<Resource[]> {
  if (conversationResourcesUnavailable) return [];

  if (!conversationStripeInflight) {
    conversationStripeInflight = fetchConversationStripeResourcesInner();
  }

  try {
    return await conversationStripeInflight;
  } finally {
    conversationStripeInflight = null;
  }
}

async function fetchConversationStripeResourcesInner(): Promise<Resource[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) return [];

  const { data: rows, error } = await supabase
    .from("conversation_resources")
    .select("*")
    .eq("user_id", session.user.id)
    .eq("status", "ready")
    .in("resource_type", ["pdf", "voice_note", "docx"])
    .order("created_at", { ascending: false });

  if (error || !rows?.length) {
    if (error) {
      if (isMissingConversationResourcesRelation(error)) {
        conversationResourcesUnavailable = true;
      } else {
        console.warn("[conversationResourceService]", error.message);
      }
    }
    return [];
  }

  const typed = rows as ConvRow[];

  const results: Resource[] = [];

  for (const row of typed) {
    const path = storagePathForRow(row);
    if (!path) continue;

    const signedUrl = await refreshConversationResourceSignedUrl(path);
    if (!signedUrl) continue;

    const resourceTypeUi: Resource["type"] =
      row.resource_type === "voice_note"
        ? "audio"
        : row.resource_type === "docx"
          ? "document"
          : "pdf";

    results.push({
      id: `cr-${row.id}`,
      title: row.title,
      description: resourceCardDescription(row.resource_type),
      meta: resourceTypeLabel(row.resource_type),
      type: resourceTypeUi,
      url: signedUrl,
      photo: await resolveCardPhoto(row),
      addedDate: row.created_at,
      category: "From chats",
      storagePath: path,
    });
  }

  return results;
}

/**
 * Card thumbnail: the AI-generated image for this resource when present,
 * otherwise a deterministic stock photo (older rows / image generation off).
 */
async function resolveCardPhoto(row: ConvRow): Promise<string> {
  const imagePath = row.image_storage_path;
  if (imagePath) {
    const { data: signed, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(imagePath, 86_400);
    if (!error && signed?.signedUrl) return signed.signedUrl;
    console.warn("[conversationResourceService] card image signed URL:", error?.message);
  }
  return pickPhoto(row.id);
}

export async function invokeCreateConversationResource(params: {
  sessionId: string;
  resourceType: ConversationResourceInvokeType;
  /** Local transcript; preferred over persisted `session_chat_histories` when provided. */
  transcriptLines?: { role: "user" | "assistant"; content: string }[];
}): Promise<{ id: string; title: string; resource_type: string; url: string | null }> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) throw new Error("Sign in required to export resources.");

  const body = {
    session_id: params.sessionId,
    resource_type: params.resourceType,
    transcript_lines: params.transcriptLines ?? undefined,
  };

  const invokeResult = await supabase.functions.invoke("create-conversation-resource", {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });
  const { data, error } = invokeResult;

  if (error) {
    const detail = await readSupabaseInvokeError(invokeResult);
    throw new Error(detail || error.message || "Could not reach export service.");
  }

  const payload = data as {
    ok?: boolean;
    error?: string;
    id?: string;
    title?: string;
    resource_type?: string;
    url?: string | null;
  } | null;
  if (payload?.error) {
    throw new Error(payload.error);
  }
  if (!payload?.ok || !payload.id || !payload.title) {
    throw new Error("Unexpected response from export service.");
  }

  return {
    id: payload.id,
    title: payload.title,
    resource_type: payload.resource_type ?? params.resourceType,
    url: payload.url ?? null,
  };
}
